import React, { useState, useEffect } from 'react';
import { SavedCarScenario, CarComparisonData } from '../types/carComparison';
import { TylerState, updateReact, useTylerStateUpdate } from '../utils/TylerState';
import carScenariosService from '../services/car-scenarios';

interface CarSavedScenariosProps {
  comparisonMode?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user: any | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  guestScenario?: any;
  currentData?: CarComparisonData;
  onLoadScenarioToA?: (data: CarComparisonData, scenarioName?: string, timestamp?: number) => void;
  onLoadScenarioToB?: (data: CarComparisonData, scenarioName?: string, timestamp?: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSaveScenario?: (data: any) => void;
  onInputChange?: (field: keyof CarComparisonData, value: any) => void; // NEW: To sync data.scenarioName
  side?: 'A' | 'B'; // Which side this component is for in comparison mode
}

const CarSavedScenarios: React.FC<CarSavedScenariosProps> = ({
  comparisonMode = false,
  user,
  guestScenario,
  currentData,
  onLoadScenarioToA,
  onLoadScenarioToB,
  onSaveScenario,
  onInputChange,
  side = 'A'
}) => {
  // Subscribe to TylerState updates
  useTylerStateUpdate();

  const [savedScenarios, setSavedScenarios] = useState<SavedCarScenario[]>([]);
  const [mode, setMode] = useState<'new' | 'saved'>('new');
  const [selectedScenario, setSelectedScenario] = useState<string>('');
  const [saveNameError, setSaveNameError] = useState<boolean>(false);

  // Get the appropriate car name from TylerState based on side
  const saveName = side === 'A' ? TylerState.carNameA : TylerState.carNameB;
  const setSaveName = (value: string) => {
    if (side === 'A') {
      TylerState.carNameA = value;
    } else {
      TylerState.carNameB = value;
    }
    updateReact();
  };

  // Load saved scenarios from backend
  const loadSavedScenarios = async () => {
    const currentUser = TylerState.user;
    if (!currentUser?.email) {
      setSavedScenarios([]);
      return;
    }
    try {
      const scenariosArray = await carScenariosService.getCarScenariosArray();
      setSavedScenarios(scenariosArray);
    } catch (error) {
      console.error('Error loading scenarios:', error);
      setSavedScenarios([]);
    }
  };

  useEffect(() => {
    loadSavedScenarios();
  }, [TylerState.user?.email]);

  const handleSave = async () => {
    const name = saveName.trim();

    // Require a name to be provided
    if (!name) {
      setSaveNameError(true);
      return;
    }

    // Clear any previous error state
    setSaveNameError(false);

    const existingScenario = savedScenarios.find(s => s.name.toLowerCase() === name.toLowerCase());

    try {
      if (existingScenario) {
        // Update existing scenario
        await carScenariosService.updateCarScenario(existingScenario.id, {
          name: name,
          data: currentData || {} as CarComparisonData,
          description: `Updated on ${new Date().toLocaleDateString()}`
        });
      } else {
        // Create new scenario
        await carScenariosService.saveCarScenario({
          name: name,
          data: currentData || {} as CarComparisonData,
          description: `Saved on ${new Date().toLocaleDateString()}`
        });
      }

      // Reload scenarios from backend
      await loadSavedScenarios();

      // Notify parent component
      if (onSaveScenario) {
        onSaveScenario({ name, data: currentData });
      }
    } catch (error) {
      console.error('Error saving scenario:', error);
    }
  };

  const handleLoad = () => {
    if (!selectedScenario) return;
    const scenario = savedScenarios.find(s => s.id === selectedScenario);
    if (scenario && onLoadScenarioToA) {
      onLoadScenarioToA(scenario.data);
    }
  };

  const handleDelete = async () => {
    if (!selectedScenario) return;
    try {
      await carScenariosService.deleteCarScenario(selectedScenario);
      await loadSavedScenarios();
      setSelectedScenario('');
    } catch (error) {
      console.error('Error deleting scenario:', error);
    }
  };

  const formatTimestamp = (timestamp: number) => {
    // Use user's local timezone instead of hardcoded Toronto
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const selectedScenarioData = savedScenarios.find(s => s.id === selectedScenario);

  return (
    <div className="scenarios-section" style={{
      background: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      padding: '16px',
      margin: '16px 0 24px'
    }}>
      <h3 style={{ margin: '0 0 16px 0', fontSize: '1.2rem', fontWeight: '600', color: '#374151' }}>
        Scenarios
      </h3>
      
      {/* Scenario Name Input and Save Button */}
      {TylerState.user ? (
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <input
            type="text"
            placeholder="Scenario Name *"
            value={saveName}
            onChange={(e) => {
              const newName = e.target.value;

              // 1. Update local state
              setSaveName(newName);

              // 2. Update TylerState (single source of truth)
              if (side === 'A') {
                TylerState.carNameA = newName;
              } else {
                TylerState.carNameB = newName;
              }
              updateReact();

              // 3. Update parent's data.scenarioName
              if (onInputChange) {
                onInputChange('scenarioName', newName || (side === 'A' ? 'Car A' : 'Car B'));
              }

              if (saveNameError) {
                setSaveNameError(false);
              }
            }}
            className={saveNameError ? 'scenario-input-error' : ''}
            style={{
              flex: 1,
              padding: '8px 12px',
              border: saveNameError ? '1px solid #dc2626' : '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif'
            }}
          />
          <button
            onClick={handleSave}
            style={{
              padding: '8px 16px',
              fontSize: '0.9rem',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Save
          </button>
        </div>
      ) : (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '16px',
          padding: '12px',
          background: '#f3f4f6',
          borderRadius: '6px',
          border: '1px solid #d1d5db'
        }}>
          <span style={{ 
            flex: 1, 
            fontSize: '0.9rem', 
            color: '#6b7280',
            fontStyle: 'italic',
            padding: '12px'
          }}>
            💾 Create an account to save scenarios
          </span>
          <button
            onClick={() => window.dispatchEvent(new Event('open-login-modal'))}
            style={{
              padding: '8px 16px',
              fontSize: '0.9rem',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Create Account
          </button>
        </div>
      )}

      {/* Saved Scenarios List */}
      {savedScenarios.length > 0 && (
        <div style={{ display: 'grid', gap: '12px' }}>
          {savedScenarios.map(scenario => (
            <div key={scenario.id} style={{ 
              background: 'white', 
              border: '1px solid #e5e7eb', 
              borderRadius: '8px', 
              padding: '12px 16px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
                    {scenario.name}
                  </h4>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    {formatTimestamp(scenario.createdAt)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                  <button
                    onClick={() => {
                      // Update TylerState with the scenario name
                      if (side === 'A') {
                        TylerState.carNameA = scenario.name;
                      } else {
                        TylerState.carNameB = scenario.name;
                      }
                      updateReact();

                      // Get the timestamp from either lastModified or createdAt
                      const timestamp = scenario.lastModified
                        ? new Date(scenario.lastModified).getTime()
                        : scenario.createdAt;

                      // In comparison mode, load to the appropriate side based on the side prop
                      if (comparisonMode && side === 'B' && onLoadScenarioToB) {
                        onLoadScenarioToB(scenario.data, scenario.name, timestamp);
                      } else if (onLoadScenarioToA) {
                        onLoadScenarioToA(scenario.data, scenario.name, timestamp);
                      }
                    }}
                    style={{
                      padding: '6px 12px',
                      fontSize: '0.85rem',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Load
                  </button>
                  <button
                    onClick={async () => {
                      if (window.confirm(`Are you sure you want to delete "${scenario.name}"?`)) {
                        try {
                          await carScenariosService.deleteCarScenario(scenario.id);
                          await loadSavedScenarios();
                        } catch (error) {
                          console.error('Error deleting scenario:', error);
                        }
                      }
                    }}
                    style={{
                      padding: '6px 12px',
                      fontSize: '0.85rem',
                      background: '#fee2e2',
                      color: '#dc2626',
                      border: '1px solid #fecaca',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No scenarios message */}
      {savedScenarios.length === 0 && TylerState.user && (
        <div style={{ 
          textAlign: 'center', 
          color: '#6b7280', 
          fontSize: '0.9rem',
          padding: '20px',
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          No saved scenarios yet. Configure your car analysis and click Save.
        </div>
      )}

      {/* Guest user message */}
      {!TylerState.user && (
        <div style={{ 
          textAlign: 'center', 
          color: '#6b7280', 
          fontSize: '0.9rem',
          padding: '20px',
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          Create an account to save and manage multiple car scenarios.
        </div>
      )}
    </div>
  );
};

export default CarSavedScenarios;
